import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import { IconCheck } from './icons';
import { cn, focusRing } from './utils';

export interface DropdownMenuItemDefinition {
  readonly id: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly onSelect?: () => void;
  readonly disabled?: boolean;
  readonly tone?: 'default' | 'danger';
  /** Explica por que o item está desabilitado. Vira texto sob o rótulo. */
  readonly disabledReason?: string;
  readonly shortcut?: string;
}

export interface DropdownMenuSectionDefinition {
  readonly id: string;
  readonly label?: string;
  readonly items: readonly DropdownMenuItemDefinition[];
}

export interface DropdownMenuProps {
  readonly trigger: ReactNode;
  readonly sections: readonly DropdownMenuSectionDefinition[];
  readonly align?: 'start' | 'center' | 'end';
  readonly ariaLabel?: string;
}

/**
 * Menu suspenso de ações.
 *
 * Item desabilitado NÃO é removido: `disabledReason` explica por que a ação não
 * está disponível. Em ambiente regulado, sumir com o botão "Aprovar" porque o
 * usuário é o autor gera chamado; dizer "Indisponível: o autor não pode aprovar
 * a própria versão" ensina a regra (ISO 13485 §4.2.4, segregação de funções).
 * O Radix mantém itens desabilitados navegáveis por seta, então o texto é lido.
 */
export function DropdownMenu({
  trigger,
  sections,
  align = 'end',
  ariaLabel,
}: DropdownMenuProps) {
  return (
    <DropdownPrimitive.Root>
      <DropdownPrimitive.Trigger asChild>{trigger}</DropdownPrimitive.Trigger>
      <DropdownPrimitive.Portal>
        <DropdownPrimitive.Content
          align={align}
          sideOffset={4}
          collisionPadding={8}
          aria-label={ariaLabel}
          className={cn(
            'z-dropdown min-w-[13rem] overflow-hidden rounded-md border border-border bg-surface-raised p-1 shadow-lg',
            'animate-content-in',
          )}
        >
          {sections.map((section, sectionIndex) => (
            <DropdownPrimitive.Group key={section.id}>
              {sectionIndex > 0 ? (
                <DropdownPrimitive.Separator className="my-1 h-px bg-border-subtle" />
              ) : null}
              {section.label ? (
                <DropdownPrimitive.Label className="px-2 py-1.5 text-2xs font-semibold uppercase tracking-wide text-content-subtle">
                  {section.label}
                </DropdownPrimitive.Label>
              ) : null}
              {section.items.map((item) => (
                <DropdownPrimitive.Item
                  key={item.id}
                  disabled={item.disabled}
                  onSelect={item.onSelect}
                  className={cn(
                    'flex cursor-pointer select-none items-start gap-2 rounded-sm px-2 py-2 text-sm outline-none',
                    'data-[highlighted]:bg-brand-subtle',
                    item.tone === 'danger'
                      ? 'text-danger-content data-[highlighted]:bg-danger-subtle'
                      : 'text-content',
                    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60',
                  )}
                >
                  {item.icon ? (
                    <span aria-hidden="true" className="mt-0.5 shrink-0">
                      {item.icon}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1">
                    <span className="block">{item.label}</span>
                    {item.disabled && item.disabledReason ? (
                      <span className="mt-0.5 block text-xs text-content-subtle">
                        Indisponível: {item.disabledReason}
                      </span>
                    ) : null}
                  </span>
                  {item.shortcut ? (
                    <span className="shrink-0 text-2xs text-content-subtle">{item.shortcut}</span>
                  ) : null}
                </DropdownPrimitive.Item>
              ))}
            </DropdownPrimitive.Group>
          ))}
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}

/** Item de menu com marca de seleção — usado no seletor de tema e de idioma. */
export function DropdownCheckItem({
  checked,
  onCheckedChange,
  children,
}: {
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly children: ReactNode;
}) {
  return (
    <DropdownPrimitive.CheckboxItem
      checked={checked}
      onCheckedChange={onCheckedChange}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-2 pl-7 text-sm text-content outline-none',
        'data-[highlighted]:bg-brand-subtle',
        focusRing,
      )}
    >
      <DropdownPrimitive.ItemIndicator className="absolute left-2">
        <IconCheck size={14} className="text-brand" />
      </DropdownPrimitive.ItemIndicator>
      {children}
    </DropdownPrimitive.CheckboxItem>
  );
}
